export default class XSwiper extends HTMLElement {
  #observer;
  #initialised = false;
  swiper = null;

  /* ───────────────────────── constructor ─────────────────────────────── */
  constructor() {
    super();

    // Only parse tiny bits of state here – no DOM queries yet.
    this.slidesPerView = this.dataset.slidesPerView ? parseInt(this.dataset.slidesPerView, 10) : 'auto';

    this.maxSlides = this.dataset.maxSlides ? parseInt(this.dataset.maxSlides, 10) : false;

    this.spaceBetween = this.dataset.spaceBetween ? parseInt(this.dataset.spaceBetween, 10) : 16;
    this.mobileSpaceBetween = this.dataset.mobileSpaceBetween
      ? parseInt(this.dataset.mobileSpaceBetween, 10)
      : this.spaceBetween;

    this.skipInit = this.hasAttribute('data-skip-init');
  }

  /* ───────────────────── lifecycle: connected ────────────────────────── */
  connectedCallback() {
    if (this.skipInit) {
      return;
    }
    // Give the main thread some breathing room
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => this.#attachLazyInit(), { timeout: 0 });
    } else {
      setTimeout(() => this.#attachLazyInit(), 0);
    }
  }

  /* ─────────────────────── lifecycle: disconnected ───────────────────── */
  disconnectedCallback() {
    this.#observer?.disconnect();
    this.swiper?.destroy();
  }

  /* ─────────────────── lazy-init helper methods ──────────────────────── */
  #attachLazyInit() {
    // Bypass lazy mode when explicitly requested
    if (this.hasAttribute('data-no-lazy')) {
      this.#initImmediately();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.#initImmediately();
            this.#observer.disconnect();
          }
        });
      },
      { rootMargin: '200px' } // start ~1 viewport ahead
    );

    this.#observer.observe(this);
  }

  init() {
    if (this.#initialised) return; // idempotent
    this.#initImmediately();
  }

  async #initImmediately() {
    if (this.#initialised) return; // idempotent
    this.#initialised = true;
    this.#createSwiper();
  }

  /* ────────────────────── Swiper setup / helpers ─────────────────────── */
  #createSwiper() {
    // Ensure correct root class
    this.classList.add('swiper');

    const totalSlides = this.querySelectorAll('.swiper-slide').length;
    if (this.maxSlides && this.slidesPerView === 'auto') {
      this.slidesPerView = Math.min(this.maxSlides, totalSlides);
    }

    this.swiper = new Swiper(this, {
      slidesPerView: this.slidesPerView,
      slidesPerGroup: this.slidesPerView === 'auto' ? 1 : this.slidesPerView,
      spaceBetween: this.mobileSpaceBetween,
      mousewheel: true,
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev'),
      },
      scrollbar: {
        el: this.querySelector('.swiper-scrollbar'),
        draggable: true,
      },
      breakpoints: {
        749: { spaceBetween: this.spaceBetween },
      },
    });

    this.swiper.on('lock', () => this.classList.add('swiper-locked'));
    this.swiper.on('unlock', () => this.classList.remove('swiper-locked'));

    this.#scrollToSelected();
  }

  #scrollToSelected() {
    const selectedIndex = this.swiper.slides.findIndex((slide) => {
      const radio = slide.querySelector('input[type="radio"]');
      return (radio && radio.checked) || slide.classList.contains('active');
    });

    if (selectedIndex > 0) this.swiper.slideTo(selectedIndex, 0);
  }
}
